import React, { useState, useMemo } from 'react';
import PlannerIcons from '../../components/shared/PlannerIcons';

export default function GlobalDashboard({ projects, onSelectProject, onCreateProject, onDeleteProject, navigateTo }) {
  const [taskFilter, setTaskFilter] = useState('all'); 

  const stats = useMemo(() => {
    let eventsThisMonth = 0;
    let openTasks = 0;
    let vendorsHired = 0;
    let nextEvent = null;

    const now = new Date();
    // Set now to midnight to avoid time-of-day issues with date strings
    now.setHours(0,0,0,0);
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();

    projects.forEach(p => {
      if (p.tasks) {
          openTasks += p.tasks.filter(t => !t.completed).length;
      }

      if (p.vendors) {
          vendorsHired += p.vendors.filter(v => v.status.includes('Booked')).length;
      }

      if (p.date && p.date !== 'TBD') {
        // Ensure date parsing treats the string as local date rather than UTC
        const [year, month, day] = p.date.split('-');
        const pDate = new Date(year, month - 1, day);
        
        if (pDate.getMonth() === currentMonth && pDate.getFullYear() === currentYear) {
            eventsThisMonth++;
        }

        if (pDate >= now) {
          if (!nextEvent || pDate < new Date(nextEvent.date.split('-')[0], nextEvent.date.split('-')[1] - 1, nextEvent.date.split('-')[2])) {
            nextEvent = p;
          }
        }
      }
    });

    return {
      activeProjects: projects.length,
      eventsThisMonth,
      openTasks,
      vendorsHired,
      nextEvent
    };
  }, [projects]);

  const crossProjectTasks = useMemo(() => {
    const allTasks = [];
    projects.forEach(p => {
      if (taskFilter === 'all' || taskFilter === p.id) {
        (p.tasks || []).forEach(t => {
          if (!t.completed) {
            allTasks.push({
              ...t,
              projectId: p.id,
              projectName: p.clientNames
            });
          }
        });
      }
    });
    // Return top 5 recent tasks
    return allTasks.slice(0, 5);
  }, [projects, taskFilter]);

  return (
    <div className="p-4 md:p-8 animate-fade-in h-full overflow-y-auto">
      <div className="max-w-7xl mx-auto space-y-8">
        
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-end gap-4">
          <div>
            <h1 className="text-2xl md:text-4xl font-serif font-bold text-gray-900">Pro Dashboard</h1>
            <p className="text-sm md:text-base text-gray-500 mt-1">Unified overview of all your active clients and events.</p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
            <button onClick={() => navigateTo('listings')} className="bg-white text-gray-900 border border-gray-200 hover:bg-gray-100 px-5 py-2.5 rounded-lg font-bold transition shadow-sm flex items-center justify-center gap-2 w-full sm:w-auto">
              <PlannerIcons.Storefront /> Browse Marketplace
            </button>
            <button onClick={onCreateProject} className="bg-pink-600 hover:bg-pink-700 text-white px-6 py-2.5 rounded-lg font-bold transition shadow-md flex items-center justify-center gap-2 w-full sm:w-auto">
              <PlannerIcons.Plus /> New Project
            </button>
          </div>
        </div>

        {/* Top Metrics Row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
          <div className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm min-w-0">
            <div className="flex items-center gap-3 mb-2">
                <div className="w-8 h-8 rounded-full bg-blue-50 text-blue-600 flex items-center justify-center"><PlannerIcons.Briefcase /></div>
                <span className="text-xs font-bold text-gray-400 uppercase tracking-wider truncate">Active</span>
            </div>
            <p className="text-2xl md:text-3xl font-bold text-gray-900 truncate" title={stats.activeProjects}>{stats.activeProjects}</p>
            <p className="text-xs text-gray-500 font-medium mt-1 truncate">Weddings & Events</p>
          </div>

          <div className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm border-b-4 border-b-pink-500 min-w-0">
            <div className="flex items-center gap-3 mb-2">
                <div className="w-8 h-8 rounded-full bg-pink-50 text-pink-600 flex items-center justify-center"><PlannerIcons.CalendarIcon /></div>
                <span className="text-xs font-bold text-gray-400 uppercase tracking-wider truncate">This Month</span>
            </div>
            <p className="text-2xl md:text-3xl font-bold text-gray-900 truncate" title={stats.eventsThisMonth}>{stats.eventsThisMonth}</p>
            <p className="text-xs text-gray-500 font-medium mt-1 truncate">Upcoming Events</p>
          </div>

          <div className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm min-w-0">
            <div className="flex items-center gap-3 mb-2">
                <div className="w-8 h-8 rounded-full bg-amber-50 text-amber-600 flex items-center justify-center"><PlannerIcons.Check /></div>
                <span className="text-xs font-bold text-gray-400 uppercase tracking-wider truncate">To-Do</span>
            </div>
            <p className="text-2xl md:text-3xl font-bold text-gray-900 truncate" title={stats.openTasks}>{stats.openTasks}</p>
            <p className="text-xs text-gray-500 font-medium mt-1 truncate">Open Tasks</p>
          </div>

          <div className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm min-w-0">
            <div className="flex items-center gap-3 mb-2">
                <div className="w-8 h-8 rounded-full bg-purple-50 text-purple-600 flex items-center justify-center"><PlannerIcons.Users /></div>
                <span className="text-xs font-bold text-gray-400 uppercase tracking-wider truncate">Team</span>
            </div>
            <p className="text-2xl md:text-3xl font-bold text-gray-900 truncate" title={stats.vendorsHired}>{stats.vendorsHired}</p>
            <p className="text-xs text-gray-500 font-medium mt-1 truncate">Vendors Hired</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column: Client Portfolio */}
          <div className="lg:col-span-2 space-y-6">
            <div className="flex justify-between items-center">
                <h2 className="text-xl font-bold text-gray-900">Client Portfolio</h2>
            </div>
            
            {projects.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  {projects.map(project => {
                    const daysUntil = project.date && project.date !== 'TBD' 
                        ? Math.ceil((new Date(project.date) - new Date()) / (1000 * 60 * 60 * 24)) 
                        : '?';
                    const safeTasks = project.tasks || [];
                    const completedTasks = safeTasks.filter(t => t.completed).length;
                    const progress = safeTasks.length ? Math.round((completedTasks / safeTasks.length) * 100) : 0;

                    return (
                      <div 
                        key={project.id} 
                        onClick={() => onSelectProject(project.id)}
                        className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-xl hover:border-pink-300 transition-all cursor-pointer group flex flex-col relative"
                      >
                        <div className="h-32 overflow-hidden relative">
                          <img src={project.coverImage} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" alt="Cover" />
                          <div className="absolute inset-0 bg-gradient-to-t from-gray-900/60 to-transparent" />
                          <div className="absolute bottom-3 left-4 right-4 flex justify-between items-end">
                              <h3 className="font-serif text-lg font-bold text-white drop-shadow-md truncate pr-2">{project.clientNames}</h3>
                              <span className="bg-pink-600/90 backdrop-blur-sm text-white px-2 py-1 rounded text-[10px] font-bold whitespace-nowrap shadow-sm">
                                {daysUntil} Days
                              </span>
                          </div>
                          <button 
                            onClick={(e) => { e.stopPropagation(); onDeleteProject(project.id); }}
                            className="absolute top-2 right-2 bg-black/40 text-white hover:bg-red-500 p-1.5 rounded-full backdrop-blur-sm opacity-0 group-hover:opacity-100 transition-all"
                            title="Delete Project"
                          >
                            <PlannerIcons.Trash />
                          </button>
                        </div>
                        <div className="p-4 flex-1 flex flex-col">
                          <p className="text-xs text-gray-500 font-medium flex items-center gap-1.5 mb-4">
                              <PlannerIcons.Clock /> {project.date !== 'TBD' ? new Date(project.date).toLocaleDateString() : 'Date TBD'}
                          </p>
                          
                          <div className="mt-auto">
                            <div className="flex justify-between text-[10px] text-gray-500 mb-1.5 font-bold uppercase tracking-wide">
                              <span>Planning Progress</span>
                              <span className="text-pink-600">{progress}%</span>
                            </div>
                            <div className="h-1.5 w-full bg-gray-100 rounded-full overflow-hidden">
                              <div className="h-full bg-pink-500 rounded-full transition-all" style={{ width: `${progress}%` }} />
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
            ) : (
                <div className="text-center py-16 bg-white rounded-xl border-2 border-dashed border-gray-200">
                    <div className="text-4xl mb-3">📁</div>
                    <h3 className="text-lg font-bold text-gray-900">No active projects</h3>
                    <p className="text-gray-500 text-sm mt-1 mb-4">Start by adding your first client wedding.</p>
                    <button onClick={onCreateProject} className="bg-pink-50 text-pink-700 px-4 py-2 rounded-lg font-bold hover:bg-pink-100 transition text-sm">
                        Create Project
                    </button>
                </div>
            )}
          </div>

          {/* Right Column: Next Up & Cross-Project Tasks */}
          <div className="space-y-6">
              
              {stats.nextEvent && (
                  <div className="bg-gradient-to-br from-gray-900 to-gray-800 p-6 rounded-xl shadow-lg text-white relative overflow-hidden">
                      <div className="absolute top-0 right-0 w-32 h-32 bg-pink-500 rounded-bl-full opacity-20 blur-2xl"></div>
                      <h3 className="text-xs font-bold text-pink-400 uppercase tracking-widest mb-4">Next Up</h3>
                      <h4 className="text-2xl font-serif font-bold mb-1 truncate">{stats.nextEvent.clientNames}</h4>
                      {/* Ensure we use local date parts so it doesn't shift by timezone */}
                      <p className="text-gray-300 text-sm mb-4">
                        {new Date(
                            stats.nextEvent.date.split('-')[0], 
                            stats.nextEvent.date.split('-')[1] - 1, 
                            stats.nextEvent.date.split('-')[2]
                        ).toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' })}
                      </p>
                      <button onClick={() => onSelectProject(stats.nextEvent.id)} className="w-full bg-white/10 hover:bg-white/20 border border-white/20 backdrop-blur-sm py-2 rounded-lg text-sm font-bold transition">
                          Open Workspace &rarr;
                      </button>
                  </div>
              )}

              <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
                  <div className="p-4 border-b border-gray-100 flex justify-between items-center gap-2 flex-wrap">
                      <h3 className="font-bold text-gray-900">Priority Tasks</h3>
                      <select 
                          value={taskFilter} 
                          onChange={(e) => setTaskFilter(e.target.value)}
                          className="text-xs bg-gray-50 border border-gray-200 text-gray-700 rounded-md px-2 py-1.5 outline-none focus:border-pink-400 hover:bg-white transition cursor-pointer max-w-[140px] sm:max-w-xs truncate"
                      >
                          <option value="all">All Projects</option>
                          {projects.map(p => (
                              <option key={p.id} value={p.id}>{p.clientNames}</option>
                          ))}
                      </select>
                  </div>
                  <div className="p-2">
                      {crossProjectTasks.length > 0 ? crossProjectTasks.map((task, i) => (
                          <div key={i} onClick={() => onSelectProject(task.projectId)} className="flex items-start gap-3 p-3 hover:bg-gray-50 rounded-lg cursor-pointer transition group">
                              <div className="w-4 h-4 rounded border-2 border-gray-300 mt-0.5 flex-shrink-0 group-hover:border-pink-400 transition" />
                              <div className="min-w-0 flex-1">
                                  <p className="text-sm font-medium text-gray-800 truncate">{task.text}</p>
                                  <p className="text-[10px] text-gray-400 uppercase tracking-wide mt-0.5 truncate">{task.projectName}</p>
                              </div>
                          </div>
                      )) : (
                          <p className="p-4 text-center text-sm text-gray-500 italic">No pending tasks found.</p>
                      )}
                  </div>
              </div>
          </div>
        </div>

      </div>
    </div>
  );
}